// ─── LoadingState ───────────────────────────────────────────────────────────
// Shown while something is loading. One look everywhere: a small green
// spinner and a short sentence that says WHAT is loading.
//
//   <LoadingState label="Loading your materials…" />            fills a page or panel
//   <LoadingState label="Loading options…" size="inline" />     sits on one line
//
// It waits 300ms before appearing. Most loads finish faster than that, and a
// spinner that flashes on and off looks like a glitch.

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { GREEN, MUTED, bodyFont } from '../../../theme/tokens2';

export default function LoadingState({ label = 'Loading…', size = 'panel', delay = 300 }) {
  const [visible, setVisible] = useState(delay === 0);

  useEffect(() => {
    if (delay === 0) return undefined;
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  const isInline = size === 'inline';

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: 'flex',
        flexDirection: isInline ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isInline ? 1 : 1.5,
        py: isInline ? 0 : size === 'page' ? 12 : 6,
        px: isInline ? 0 : 3,
        minHeight: size === 'page' ? '50vh' : undefined,
        // Keep the space reserved during the delay so nothing jumps.
        visibility: visible ? 'visible' : 'hidden',
      }}
    >
      <CircularProgress size={isInline ? 14 : 20} thickness={4.5} sx={{ color: GREEN }} />
      <Typography sx={{ ...bodyFont, fontSize: isInline ? '12px' : '13px', color: MUTED }}>
        {label}
      </Typography>
    </Box>
  );
}
